import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import styles from '@styles/common/Snackbar.module.css';

interface SnackbarProps {
  open?: boolean;
  message: string;
  variant?: 'success' | 'warning' | 'error' | 'info';
  position?: 'top' | 'center' | 'bottom';
  dismissable?: boolean;
  duration?: number | 'permanent';
  onClose?: () => void;
  className?: string;
  showSpinner?: boolean;
  actionLabel?: string;
  onAction?: () => void;
}

/**
 * Snackbar - A toast notification component
 *
 * @param message - The message to display
 * @param variant - The color variant: "success", "warning", "error", or "info" (default: "info")
 * @param position - Position on screen: "top", "center", or "bottom" (default: "bottom")
 * @param dismissable - Whether the snackbar can be manually closed (default: true)
 * @param duration - Auto-dismiss duration in milliseconds or "permanent" (default: 5000ms)
 * @param onClose - Callback function when the snackbar is closed
 * @param className - Optional additional CSS class
 * @param showSpinner - Whether to show a loading spinner (default: false)
 * @param actionLabel - Optional action button label (e.g., "Retry")
 * @param onAction - Optional action button callback function
 *
 * @example
 * <Snackbar message="Success!" variant="success" />
 *
 * @example
 * <Snackbar message="Warning" variant="warning" position="center" dismissable={false} />
 *
 * @example
 * <Snackbar message="Error occurred" variant="error" duration="permanent" />
 *
 * @example
 * <Snackbar message="Loading..." showSpinner position="center" dismissable={false} />
 *
 * @example
 * <Snackbar message="Error!" variant="error" actionLabel="Retry" onAction={handleRetry} />
 */
const Snackbar: React.FC<SnackbarProps> = ({
  open = true,
  message,
  variant = 'info',
  position = 'bottom',
  dismissable = true,
  duration = 5000,
  onClose,
  className,
  showSpinner = false,
  actionLabel,
  onAction,
}) => {
  useEffect(() => {
    if (open && duration !== 'permanent' && duration > 0 && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [open, duration, onClose]);

  if (!open) {
    return null;
  }

  const handleClose = () => {
    if (dismissable && onClose) {
      onClose();
    }
  };

  const handleAction = () => {
    if (onAction) {
      onAction();
    }
  };

  const snackbarClass = `${styles.snackbar} ${styles[variant]} ${styles[position]} ${className || ''}`;

  return (
    <div
      className={snackbarClass}
      role='alert'
      aria-live='assertive'
      aria-atomic='true'
    >
      {showSpinner && (
        <div className={styles.spinner} aria-hidden='true'>
          <div className={styles.spinnerCircle}></div>
        </div>
      )}
      <span className={styles.message}>{message}</span>
      <div className={styles.actions}>
        {actionLabel && onAction && (
          <button
            className={styles.actionButton}
            onClick={handleAction}
            type='button'
          >
            {actionLabel}
          </button>
        )}
        {dismissable && (
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label='Close'
            type='button'
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export default Snackbar;
